import type React from 'react';
import { type FC, Suspense, useEffect, useState } from 'react';

import classNames from 'classnames';

import { getOS } from '@iconik-io/core/src/os/get_os';
import type { SuggestionsPluginItem } from '@iconik-io/uad';

import { FLAG_SIMPLIFIED_ACLS } from '../../../../analytics/flags';
import { growthbook } from '../../../../analytics/growthbook';
import { lazyComponent } from '../../../utils/lazy_component';
import { MentionsList } from '../mentions_list/mentions_list';

import styles from './comment_input.module.css';

// Keeps ProseMirror out of the initial bundle; it is only needed once a
// comment field is rendered.
const Editor = lazyComponent(() => import('./editor'), 'Editor');

type Props = {
  className?: string;
  value: string;
  onChange: (value: string) => void;
  color?: string;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  onLayoutChanged: () => void;
  onSubmitRequested: () => void;
  onFocus?: () => void;
  canMention: boolean;
  filterMentionResults?: (
    query: string | null,
  ) => Promise<SuggestionsPluginItem[]>;
  onClick?: () => void;
  // When this counter changes, reinitialize the Editor view
  resetCounter?: number;
};

export const CommentInput: FC<Props> = (props) => {
  // TODO replace with real values, now in iconik:
  // mentionsMode = usersAndEmails only on share page.
  // const mentionsMode = 'usersAndGroups';

  const {
    className,
    disabled,
    onChange,
    onLayoutChanged,
    onSubmitRequested,
    value,
    autoFocus,
    onFocus,
    canMention,
    filterMentionResults,
    onClick,
    resetCounter,
  } = props;

  const getPlaceholder = () => {
    if (props.placeholder !== undefined) {
      return props.placeholder;
    }
    if (!canMention) {
      return 'Add comment here...';
    }
    return growthbook?.isOn(FLAG_SIMPLIFIED_ACLS)
      ? 'Add comment here. Type @ to mention a team or user...'
      : 'Add comment here. Type @ to mention a group or user...';
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    const isMacOS = getOS() === 'MacOS';

    if (isMacOS && event.metaKey && event.key === 'Enter') {
      onSubmitRequested?.();
    }
    if (!isMacOS && event.ctrlKey && event.key === 'Enter') {
      onSubmitRequested?.();
    }
  };

  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (resetCounter !== undefined) {
      setEditorKey((prev) => prev + 1);
    }
  }, [resetCounter]);

  return (
    <>
      {/* Editor reads `disabled` only when its view is created, so the key carries it */}
      <Suspense fallback={null}>
        <Editor
          key={`${editorKey}_${disabled}`}
          className={classNames(styles.text_input, className)}
          disabled={disabled}
          content={value}
          placeholder={getPlaceholder()}
          aria-label="Comment input"
          onChange={(newValue) => {
            onChange(newValue);
            onLayoutChanged?.();
          }}
          onKeyDown={handleKeyDown}
          canMention={canMention}
          autoFocus={autoFocus}
          filterMentionResults={filterMentionResults}
          suggestionsListComponent={MentionsList}
          onFocus={() => {
            onFocus?.();
          }}
          onClick={onClick}
        />
      </Suspense>
      <input
        type="hidden"
        name="segment_text"
        value={value}
        disabled={disabled}
      />
    </>
  );
};
